/**
 * A file's bytes made ready to sync: hashed, and gzipped unless the server already holds them. The
 * browser does it in a worker, off the page's main thread; anywhere without workers (tests, a
 * worker that fails) it runs inline.
 */
import { gzip, sha256Hex } from "../core/bytes";

export interface Prepared {
  sha256: string;
  /** gzip; null when the bytes hash to `knownSha256`, so there is nothing to send. */
  body: Uint8Array | null;
}

export type Prepare = (bytes: Uint8Array, knownSha256?: string) => Promise<Prepared>;

export const prepareInline: Prepare = async (bytes, knownSha256) => {
  const sha256 = await sha256Hex(bytes);
  return { sha256, body: sha256 === knownSha256 ? null : await gzip(bytes) };
};

export interface PrepareRequest {
  id: number;
  bytes: Uint8Array;
  knownSha256?: string;
}

export type PrepareReply = { id: number } & (
  | { ok: true; prepared: Prepared }
  | { ok: false; message: string }
);

/** What workerPrepare needs of a Worker (tests hand it fakes). */
export interface PrepareWorker {
  postMessage(request: PrepareRequest): void;
  terminate(): void;
  onmessage: ((event: MessageEvent<PrepareReply>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
}

export interface WorkerPrepareOptions {
  /** Starts the worker; null when there is none to start. */
  start?: () => Promise<PrepareWorker | null>;
  /** How long a request of `size` bytes may wait for its reply. */
  replyTimeoutMs?: (size: number) => number;
  log?: Pick<Console, "warn">;
}

const MB = 1024 * 1024;

async function startModuleWorker(): Promise<PrepareWorker | null> {
  if (typeof Worker === "undefined") return null;
  try {
    const { default: ModuleWorker } = await import("./prepare.worker?worker");
    return new ModuleWorker();
  } catch {
    return null;
  }
}

/**
 * Prepares in one module worker, started on first use. The worker gets a copy of the bytes, so
 * the caller's stay usable: when the worker fails (its script did not load, it errs, a reply does
 * not come in time) it is ended, the requests waiting on it are prepared inline from those bytes,
 * and every later request runs inline too.
 */
export function workerPrepare({
  start = startModuleWorker,
  replyTimeoutMs = (size) => 30_000 + Math.ceil(size / MB) * 1_000,
  log = console,
}: WorkerPrepareOptions = {}): Prepare {
  let worker: Promise<PrepareWorker | null> | null = null;
  let live: PrepareWorker | null = null;
  const waiting = new Map<
    number,
    {
      bytes: Uint8Array;
      knownSha256?: string;
      timer: ReturnType<typeof setTimeout>;
      settle(reply: Promise<Prepared>): void;
    }
  >();
  let nextId = 1;

  function broken(reason: string) {
    if (!live) return;
    log.warn(
      `sandbox sync: the prepare worker failed (${reason}); preparing on the page from now on`
    );
    live.terminate();
    live = null;
    worker = Promise.resolve(null);
    for (const request of waiting.values()) {
      clearTimeout(request.timer);
      request.settle(prepareInline(request.bytes, request.knownSha256));
    }
    waiting.clear();
  }

  async function started(): Promise<PrepareWorker | null> {
    const running = await start();
    if (!running) return null;
    live = running;
    running.onmessage = (event) => {
      const request = waiting.get(event.data.id);
      if (running !== live || !request) return;
      waiting.delete(event.data.id);
      clearTimeout(request.timer);
      const reply = event.data;
      request.settle(
        reply.ok ? Promise.resolve(reply.prepared) : Promise.reject(new Error(reply.message))
      );
    };
    running.onerror = () => running === live && broken("an error");
    running.onmessageerror = () => running === live && broken("a reply it could not read");
    return running;
  }

  return async (bytes, knownSha256) => {
    worker ??= started();
    const running = await worker;
    if (!running) return prepareInline(bytes, knownSha256);
    const id = nextId++;
    return new Promise<Prepared>((resolve, reject) => {
      const timer = setTimeout(() => broken("no reply in time"), replyTimeoutMs(bytes.length));
      waiting.set(id, {
        bytes,
        knownSha256,
        timer,
        settle: (reply) => reply.then(resolve, reject),
      });
      running.postMessage({ id, bytes, knownSha256 });
    });
  };
}
